import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { Checkbox } from 'ui-components/components/checkbox';

import DataDrivenLogo from '../../../components/data-driven-logo';
import Global from '../../../store/global-selectors';
import { SaveConfirmationModal } from './save-confirmation-modal';

import { InventoryConnectionTerminusTypeEnum, InventoryConnectionValidStatusEnum } from './enums';
import messages from './messages';

import styles from './inventory-sync-page.module.less';
import { useModal } from '../../../hooks/use-modal';
import InventorySetupPanel from './inventory-setup-panel';
import InventorySyncSetupPanel from './inventory-sync-setup-panel';

export const EditInventorySync = ({ moveableConnections, onCancel, onSave, isLoading, inventorySyncFeatureEnabled }) => {
  const [connections, setConnections] = useState([]);
  const [originalConnections, setOriginalConnections] = useState([]);
  const isAnySourceSelected = connections.some((conn) => conn.isCheckedSource);
  const isAnyTargetSelected = connections.some((conn) => conn.isCheckedTarget);

  const marketplaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const allFulfillmentProviders = useSelector(Global.selectReferenceDatagetFulfillmentProviders);

  const memoizedMarketplaces = useMemo(() => marketplaces, [marketplaces]);
  const memoizedFulfillmentProviders = useMemo(() => allFulfillmentProviders, [allFulfillmentProviders]);

  useEffect(() => {
    const updatedConnections = moveableConnections.map((connection) => ({
      ...connection,
      isCheckedSource: connection.terminusType === InventoryConnectionTerminusTypeEnum.Source,
      isCheckedTarget: connection.terminusType === InventoryConnectionTerminusTypeEnum.Target,
      isDisabledSource: connection.terminusType === InventoryConnectionTerminusTypeEnum.Target,
      isDisabledTarget: connection.terminusType === InventoryConnectionTerminusTypeEnum.Source,
    }));
    setConnections(updatedConnections);
    setOriginalConnections(updatedConnections);
  }, [moveableConnections]);

  const [didUserSeeConfirmation, setDidUserSeeConfirmation] = useState(false);

  const isTheConfirmationModalNeeded = useCallback(() => {
    // just check the reference
    // further logic would still detect it on its own, but reference checking is faster
    if (originalConnections === connections) {
      return false;
    }

    // if no target is selected, then dont display the modal
    if (!isAnyTargetSelected) {
      return false;
    }

    const didUserMakeChange = originalConnections.some((originalConnection, index) => {
      const newConnection = connections[index];
      // if user added or removed a target, then display the modal
      if (originalConnection.isCheckedTarget !== newConnection.isCheckedTarget) {
        return true;
      }
      // if user unchecked a source, then display the modal
      if (originalConnection.isCheckedSource) {
        return newConnection.isCheckedSource !== true;
      }
      return false;
    });

    if (didUserMakeChange && !didUserSeeConfirmation) {
      return true;
    }

    // other changes when we don't want to show the modal:
    // - user added a source
    return false;
  }, [connections, didUserSeeConfirmation, isAnyTargetSelected, originalConnections]);

  const getLogoProps = (apiCode) => {
    const marketplace = memoizedMarketplaces.find((m) => m.apiCode === apiCode);
    if (marketplace) {
      return { marketplaceId: marketplace.marketplaceId };
    }

    const fulfillmentProvider = memoizedFulfillmentProviders.find((fp) => fp.apiCode === apiCode);
    if (fulfillmentProvider) {
      return { fillProviderId: fulfillmentProvider.fillProviderId };
    }

    return { marketplaceId: 0 };
  };

  // A user could select all sources,
  // but no targets and that is a valid configuration.
  const isValidConfiguration = isAnySourceSelected || (!isAnySourceSelected && !isAnyTargetSelected);

  const sourceConnections = useMemo(
    () =>
      connections.filter(
        (conn) =>
          conn.supportedTerminusTypes.includes(InventoryConnectionTerminusTypeEnum.Source) &&
          Object.values(InventoryConnectionValidStatusEnum).includes(conn.status.toLowerCase()),
      ),
    [connections],
  );

  const targetConnections = useMemo(
    () =>
      connections.filter(
        (conn) =>
          conn.supportedTerminusTypes.includes(InventoryConnectionTerminusTypeEnum.Target) &&
          Object.values(InventoryConnectionValidStatusEnum).includes(conn.status.toLowerCase()),
      ),
    [connections],
  );

  const toggleCheckbox = (id, type) => {
    setConnections(
      connections.map((conn) => {
        if (conn.connectionId === id) {
          const isSource = type === InventoryConnectionTerminusTypeEnum.Source;
          return {
            ...conn,
            isCheckedSource: isSource ? !conn.isCheckedSource : conn.isCheckedSource,
            isCheckedTarget: isSource ? conn.isCheckedTarget : !conn.isCheckedTarget,
            isDisabledSource: isSource ? conn.isDisabledSource : !conn.isCheckedTarget,
            isDisabledTarget: isSource ? !conn.isCheckedSource : conn.isDisabledTarget,
          };
        }
        return conn;
      }),
    );
  };

  const { open, close, isOpen } = useModal();

  const [connectionError, setConnectionError] = useState(null);

  const saveConnections = useCallback(() => onSave(connections), [onSave, connections]);

  const onOpenConfirmationModal = useCallback(() => {
    if (!isValidConfiguration) {
      setConnectionError(messages.inventorySyncEditNoSourceSelected);
    } else if (isTheConfirmationModalNeeded()) {
      setDidUserSeeConfirmation(true);
      setConnectionError(null);
      open();
    } else {
      saveConnections();
    }
  }, [isTheConfirmationModalNeeded, isValidConfiguration, open, saveConnections]);

  // Probably user did not turn on native inventory
  const userHasNoSourcesToDisplay = sourceConnections.length === 0;

  // Either they don't have the Inventory Sync add-on, or they have no order sources configured yet
  const userHasNoTargetsToDisplay = !inventorySyncFeatureEnabled || targetConnections.length === 0;

  return (
    <div className={styles['edit-inventory-sync-container']}>
      <div className={styles['edit-inventory-sync-header']}>
        <div className={styles['setup-inventory-sync-header']}>
          <FormattedMessage {...messages.inventorySyncEditConfiguration} />
        </div>
        <ul>
          <li>
            <Text textType={TextType.Paragraph} value={messages.inventorySyncEditConnectionsHelpText1} />
          </li>
          <li>
            <Text textType={TextType.Paragraph} value={messages.inventorySyncEditConnectionsHelpText2} />
          </li>
        </ul>
        {!isValidConfiguration && connectionError && (
          <div className={styles['save-disabled-error-banner']}>
            <FontAwesomeIcon className={styles['error-icon']} icon={faExclamationCircle} />
            <Text textType={TextType.Caption} value={connectionError} />
          </div>
        )}
      </div>
      <div className={styles['edit-setup-grid-body']}>
        <div className={styles['source-column']}>
          <Text textType={TextType.H4} value={messages.sourceLabel} />
          {userHasNoSourcesToDisplay ? (
            <InventorySetupPanel />
          ) : (
            sourceConnections.map((conn) => (
              <div
                key={conn.connectionId}
                className={classNames(
                  styles['checkbox-item'],
                  { [styles['checked-connection']]: conn.isCheckedSource },
                  { [styles['unchecked-connection']]: !conn.isCheckedSource },
                )}
              >
                <Checkbox
                  key={conn.connectionId}
                  ariaLabel={conn.name}
                  checked={conn.isCheckedSource}
                  className={styles.checkbox}
                  disabled={conn.isDisabledSource}
                  inputId={`${conn.connectionId}-source`}
                  onChange={(value) => {
                    if (value) {
                      setConnectionError(null);
                    }
                    toggleCheckbox(conn.connectionId, InventoryConnectionTerminusTypeEnum.Source);
                  }}
                />
                <label className={styles['checkbox-label']} htmlFor={`${conn.connectionId}-source`}>
                  <DataDrivenLogo className={styles['connection-icon']} logoType='icon' {...getLogoProps(conn.apiCode)} />
                  <span className={styles['connection-name']}>{conn.name}</span>
                </label>
              </div>
            ))
          )}
        </div>
        <FontAwesomeIcon className={styles['right-arrow']} icon={faArrowRight} />
        <div className={styles['target-column']}>
          <Text textType={TextType.H4} value={messages.targetLabel} />
          {userHasNoTargetsToDisplay ? (
            <InventorySyncSetupPanel inventorySyncFeatureEnabled={inventorySyncFeatureEnabled} isEditing />
          ) : (
            targetConnections.map((conn) => (
              <div
                key={conn.connectionId}
                className={classNames(
                  styles['checkbox-item'],
                  { [styles['checked-connection']]: conn.isCheckedTarget },
                  { [styles['unchecked-connection']]: !conn.isCheckedTarget },
                )}
              >
                <Checkbox
                  key={conn.connectionId}
                  ariaLabel={conn.name}
                  checked={conn.isCheckedTarget}
                  className={styles.checkbox}
                  disabled={conn.isDisabledTarget}
                  inputId={`${conn.connectionId}-target`}
                  onChange={() => toggleCheckbox(conn.connectionId, InventoryConnectionTerminusTypeEnum.Target)}
                />
                <label className={styles['checkbox-label']} htmlFor={`${conn.connectionId}-target`}>
                  <DataDrivenLogo className={styles['connection-icon']} logoType='icon' {...getLogoProps(conn.apiCode)} />
                  <span className={styles['connection-name']}>{conn.name}</span>
                </label>
              </div>
            ))
          )}
        </div>
      </div>
      <div className={styles['edit-inventory-sync-footer']}>
        <Button disabled={isLoading} onClick={onCancel}>
          <FormattedMessage {...messages.inventorySyncCancelEditBtn} />
        </Button>
        <Button
          // disable button if user did not touch the configuration
          disabled={isLoading || (!isValidConfiguration && !!connectionError) || connections === originalConnections}
          onClick={onOpenConfirmationModal}
          theme='primary'
        >
          <FormattedMessage {...messages.inventorySyncSaveConfigurationBtn} />
        </Button>
      </div>
      {isOpen && <SaveConfirmationModal onClose={close} onSave={saveConnections} />}
    </div>
  );
};

EditInventorySync.propTypes = {
  moveableConnections: PropTypes.arrayOf(
    PropTypes.shape({
      connectionId: PropTypes.string.isRequired,
      connectionType: PropTypes.string,
      terminusType: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      externalId: PropTypes.string,
      updatedAt: PropTypes.string,
      createdAt: PropTypes.string,
      lastRefreshedAt: PropTypes.string,
      supportedTerminusTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
      apiCode: PropTypes.string.isRequired,
      lastJobInfo: PropTypes.string,
    }),
  ).isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  inventorySyncFeatureEnabled: PropTypes.bool.isRequired,
};
